import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../components/Profile.css";

interface PlayerProfile {
  id: string;
  full_name: string;
  username: string;
  email: string;
  member_since: string;

  avatar: string | null;
  bio: string | null;

  games_played: number;
  wins: number;
  losses: number;
  draws: number;
  rating: number;
  win_rate: number;
}

import API_URL from "../config/api";

const getAvatarUrl = (
  avatarPath: string | null
) => {
  if (!avatarPath) {
    return "/avatars/user.png";
  }

  // Uploaded user photos come from backend
  if (avatarPath.startsWith("/uploads/")) {
    return `${API_URL}${avatarPath}`;
  }

  // Built-in avatars come from frontend/public
  return avatarPath;
};

const Profile: React.FC = () => {
  const navigate = useNavigate();

  const [profile, setProfile] =
    useState<PlayerProfile | null>(null);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  const [editing, setEditing] =
    useState(false);

  const [fullName, setFullName] =
    useState("");

  const [bio, setBio] =
    useState("");

  const [avatar, setAvatar] =
    useState("/avatars/user.png");

  const [saving, setSaving] =
    useState(false);

  const [message, setMessage] =
    useState("");

  const [
    uploadingAvatar,
    setUploadingAvatar,
  ] = useState(false);

  // =====================================================
  // LOAD PROFILE
  // =====================================================

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError("");

      const token =
        localStorage.getItem("token");

      if (!token) {
        navigate("/login");
        return;
      }

      const response = await fetch(
        `${API_URL}/api/profile/me`,
        {
          headers: {
            Authorization:
              `Bearer ${token}`,
          },
        }
      );

      const data =
        await response.json();

      if (!response.ok) {
        throw new Error(
          data.message ||
            "Unable to load profile."
        );
      }

      setProfile(data.profile);

      setFullName(
        data.profile.full_name || ""
      );

      setBio(
        data.profile.bio || ""
      );

      setAvatar(
        data.profile.avatar ||
          "/avatars/user.png"
      );

    } catch (err: any) {

      console.error(
        "Profile load error:",
        err
      );

      setError(
        err.message ||
          "Unable to load profile."
      );

    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

    // =====================================================
    // UPLOAD PROFILE PHOTO
    // =====================================================

    const uploadProfilePhoto = async (
    event: React.ChangeEvent<HTMLInputElement>
    ) => {
    const input = event.target;

    try {
        setError("");
        setMessage("");

        const file =
        input.files?.[0];

        if (!file) {
        return;
        }

        // ---------------------------------------------
        // VALIDATE FILE TYPE
        // ---------------------------------------------

        const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/webp",
        ];

        if (!allowedTypes.includes(file.type)) {
        throw new Error(
            "Please select a JPG, PNG, or WebP image."
        );
        }

        // ---------------------------------------------
        // VALIDATE FILE SIZE
        // ---------------------------------------------

        const maxSize =
        5 * 1024 * 1024;

        if (file.size > maxSize) {
        throw new Error(
            "Profile photo cannot be larger than 5 MB."
        );
        }

        const token =
        localStorage.getItem("token");

        if (!token) {
        navigate("/login");
        return;
        }

        setUploadingAvatar(true);

        // ---------------------------------------------
        // CREATE MULTIPART REQUEST
        // ---------------------------------------------

        const formData =
        new FormData();

        formData.append(
        "avatar",
        file
        );

        // ---------------------------------------------
        // SEND TO BACKEND
        // ---------------------------------------------

        const response =
        await fetch(
            `${API_URL}/api/profile/avatar`,
            {
            method: "POST",

            headers: {
                Authorization:
                `Bearer ${token}`,
            },

            body: formData,
            }
        );

        const data =
        await response.json();

        if (!response.ok) {
        throw new Error(
            data.message ||
            "Unable to upload profile photo."
        );
        }

        // ---------------------------------------------
        // UPDATE PROFILE
        // ---------------------------------------------

        setProfile(
        data.profile
        );

        setAvatar(
        data.profile.avatar
        );

        setMessage(
        "Profile photo uploaded successfully."
        );

    } catch (err: any) {

        console.error(
        "Avatar upload error:",
        err
        );

        setError(
        err.message ||
            "Unable to upload profile photo."
        );

    } finally {

        setUploadingAvatar(false);

        input.value = "";
    }
    };
  // =====================================================
  // SAVE PROFILE
  // =====================================================

  const saveProfile = async () => {
    try {
      setSaving(true);
      setMessage("");
      setError("");

      const token =
        localStorage.getItem("token");

      if (!token) {
        navigate("/login");
        return;
      }

      const response = await fetch(
        `${API_URL}/api/profile/me`,
        {
          method: "PUT",

          headers: {
            "Content-Type":
              "application/json",

            Authorization:
              `Bearer ${token}`,
          },

          body: JSON.stringify({
            full_name: fullName,
            bio,

            ...(avatar.startsWith("/avatars/")
                ? {
                    avatar,
                }
                : {}),
            }),
        }
      );

      const data =
        await response.json();

      if (!response.ok) {
        throw new Error(
          data.message ||
            "Unable to save profile."
        );
      }

      setProfile(
        data.profile
      );

      setEditing(false);

      setMessage(
        "Profile updated successfully."
      );

    } catch (err: any) {

      console.error(
        "Profile save error:",
        err
      );

      setError(
        err.message ||
          "Unable to save profile."
      );

    } finally {
      setSaving(false);
    }
  };

  // =====================================================
  // CANCEL EDIT
  // =====================================================

  const cancelEdit = () => {
    if (!profile) {
      return;
    }

    setFullName(
      profile.full_name || ""
    );

    setBio(
      profile.bio || ""
    );

    setAvatar(
      profile.avatar ||
        "/avatars/user.png"
    );

    setEditing(false);
    setMessage("");
  };

  // =====================================================
  // MEMBER DATE
  // =====================================================

  const formatMemberSince = (
    value: string
  ) => {
    if (!value) {
      return "Unknown";
    }

    return new Date(
      value
    ).toLocaleDateString(
      undefined,
      {
        year: "numeric",
        month: "long",
      }
    );
  };

  // =====================================================
  // LOADING
  // =====================================================

  if (loading) {
    return (
      <div className="profile-page">
        <div className="profile-loading">
          Loading profile...
        </div>
      </div>
    );
  }

  // =====================================================
  // ERROR
  // =====================================================

  if (
    error &&
    !profile
  ) {
    return (
      <div className="profile-page">
        <div className="profile-error-card">

          <h2>
            Unable to load profile
          </h2>

          <p>{error}</p>

          <button
            onClick={() =>
              navigate("/")
            }
          >
            Back to Game House
          </button>

        </div>
      </div>
    );
  }

  if (!profile) {
    return null;
  }

  // =====================================================
  // UI
  // =====================================================

  return (
    <div className="profile-page">

      <div className="profile-topbar">

        <button
          className="profile-back-btn"
          onClick={() =>
            navigate("/")
          }
        >
          ← Game House
        </button>

        <div className="profile-brand">
          ♠ P-ARENA
        </div>

      </div>

      <main className="profile-container">

        {/* ========================================= */}
        {/* PLAYER HEADER */}
        {/* ========================================= */}

        <section className="profile-card profile-hero">

          <div className="profile-avatar-wrapper">

            <img
                src={getAvatarUrl(avatar)}
                alt={profile.username}
                className="profile-avatar"
                onError={(event) => {
                    event.currentTarget.src =
                    "/avatars/user.png";
                }}
            />

            <span className="profile-online-dot" />

          </div>

          <div className="profile-identity">

            <div className="profile-level-label">
              PLAYER PROFILE
            </div>

            <h1>
              {profile.username}
            </h1>

            <div className="profile-real-name">
              {profile.full_name}
            </div>

            <div className="profile-status">
              <span />
              Online
            </div>

            <div className="profile-member">
              Member since{" "}
              {formatMemberSince(
                profile.member_since
              )}
            </div>

          </div>

          <div className="profile-rating">

            <span className="rating-label">
              P-ARENA RATING
            </span>

            <strong>
              {profile.rating}
            </strong>

            <span className="rating-caption">
              Starting rating 1000
            </span>

          </div>

        </section>

        {/* ========================================= */}
        {/* STATS */}
        {/* ========================================= */}

        <section className="profile-stats">

          <div className="profile-stat-card">
            <span>🎮</span>

            <strong>
              {profile.games_played}
            </strong>

            <p>Games Played</p>
          </div>

          <div className="profile-stat-card">
            <span>🏆</span>

            <strong>
              {profile.wins}
            </strong>

            <p>Wins</p>
          </div>

          <div className="profile-stat-card">
            <span>♦</span>

            <strong>
              {profile.losses}
            </strong>

            <p>Losses</p>
          </div>

          <div className="profile-stat-card">
            <span>🤝</span>

            <strong>
              {profile.draws}
            </strong>

            <p>Draws</p>
          </div>

          <div className="profile-stat-card highlight-stat">
            <span>📈</span>

            <strong>
              {profile.win_rate}%
            </strong>

            <p>Win Rate</p>
          </div>

        </section>

        {/* ========================================= */}
        {/* PROFILE DETAILS */}
        {/* ========================================= */}

        <section className="profile-card profile-details">

          <div className="profile-section-header">

            <div>
              <span className="profile-small-title">
                ACCOUNT
              </span>

              <h2>
                Player Information
              </h2>
            </div>

            {!editing && (
              <button
                className="profile-edit-btn"
                onClick={() => {
                  setEditing(true);
                  setMessage("");
                }}
              >
                ✎ Edit Profile
              </button>
            )}

          </div>

          {message && (
            <div className="profile-success">
              {message}
            </div>
          )}

          {error && (
            <div className="profile-inline-error">
              {error}
            </div>
          )}

          {!editing ? (

            <div className="profile-information-grid">

              <div className="profile-info-item">
                <span>
                  Full Name
                </span>

                <strong>
                  {profile.full_name}
                </strong>
              </div>

              <div className="profile-info-item">
                <span>
                  Username
                </span>

                <strong>
                  @{profile.username}
                </strong>
              </div>

              <div className="profile-info-item">
                <span>
                  Email
                </span>

                <strong>
                  {profile.email}
                </strong>
              </div>

              <div className="profile-info-item">
                <span>
                  Rating
                </span>

                <strong>
                  {profile.rating}
                </strong>
              </div>

              <div className="profile-info-item profile-bio-item">

                <span>
                  About Me
                </span>

                <strong>
                  {profile.bio ||
                    "No bio added yet."}
                </strong>

              </div>

            </div>

          ) : (

            <div className="profile-edit-form">

              <div className="profile-form-group">

                <label>
                  Full Name
                </label>

                <input
                  type="text"
                  value={fullName}
                  maxLength={100}
                  onChange={(e) =>
                    setFullName(
                      e.target.value
                    )
                  }
                />

              </div>

              <div className="profile-form-group">

                <label>
                  Bio
                </label>

                <textarea
                  value={bio}
                  maxLength={250}
                  rows={4}
                  placeholder="Tell other players a little about yourself..."
                  onChange={(e) =>
                    setBio(
                      e.target.value
                    )
                  }
                />

                <div className="profile-character-count">
                  {bio.length}/250
                </div>

              </div>

              <div className="profile-form-group">

                <label>
                    Profile Picture
                </label>

                {/* ======================================= */}
                {/* AVATAR OPTIONS                          */}
                {/* ======================================= */}

                <div className="avatar-selection">

                    {[
                    "/avatars/avatar1.png",
                    "/avatars/avatar2.png",
                    "/avatars/avatar3.png",
                    "/avatars/avatar4.png",
                    "/avatars/avatar5.png",
                    ].map(
                    (avatarPath) => (
                        <button
                        type="button"
                        key={avatarPath}
                        className={
                            avatar === avatarPath
                            ? "avatar-option selected"
                            : "avatar-option"
                        }
                        onClick={() =>
                            setAvatar(
                            avatarPath
                            )
                        }
                        >
                        <img
                            src={avatarPath}
                            alt="Avatar"
                        />
                        </button>
                    )
                    )}

                </div>

                {/* ======================================= */}
                {/* DIVIDER                                 */}
                {/* ======================================= */}

                <div className="profile-avatar-upload-divider">
                    <span>OR</span>
                </div>

                {/* ======================================= */}
                {/* UPLOAD PHOTO                            */}
                {/* ======================================= */}

                <div className="profile-avatar-upload">

                    <label
                    htmlFor="profile-avatar-file"
                    className={
                        uploadingAvatar
                        ? "profile-upload-button disabled"
                        : "profile-upload-button"
                    }
                    >
                    {uploadingAvatar
                        ? "Uploading..."
                        : "📷 Upload Your Photo"}
                    </label>

                    <input
                    id="profile-avatar-file"
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={uploadProfilePhoto}
                    disabled={uploadingAvatar}
                    hidden
                    />

                    <p className="profile-upload-help">
                    JPG, PNG or WebP • Maximum 5 MB
                    </p>

                </div>

                </div>

              <div className="profile-edit-actions">

                <button
                  className="profile-cancel-btn"
                  onClick={
                    cancelEdit
                  }
                  disabled={saving}
                >
                  Cancel
                </button>

                <button
                  className="profile-save-btn"
                  onClick={
                    saveProfile
                  }
                  disabled={
                    saving ||
                    !fullName.trim()
                  }
                >
                  {saving
                    ? "Saving..."
                    : "Save Changes"}
                </button>

              </div>

            </div>

          )}

        </section>

      </main>

    </div>
  );
};

export default Profile;